/**
 * apiErrorMessage — traduce CUALQUIER error del API a un motivo entendible.
 *
 * Por qué existe: axios pone `error.message = "Request failed with status code 423"`.
 * Si una pantalla muestra ese texto, el usuario ve un código y cree que el sistema
 * está roto — cuando en realidad el backend SÍ explicó el motivo
 * (ej: "Período cerrado hasta 31/07/2026..."). Este módulo saca siempre el texto
 * humano, y si no hay ninguno, da uno claro según el tipo de error.
 *
 * Sin dependencias a propósito: lo usan tanto el interceptor de axios como
 * errorHandler.js (Swal), sin ciclos de import.
 */

// Mensajes por código de negocio del backend (respaldo si no manda 'message').
const POR_CODIGO = {
  PERIOD_CLOSED:
    "El período contable de esta agencia ya está cerrado, por eso no se puede " +
    "modificar. Contabilidad debe reabrirlo para continuar.",
  EXTRACTO_CLOSED:
    "El extracto del vehículo de ese mes ya está cerrado. Hay que reabrirlo " +
    "para poder hacer este cambio.",
  TICKET_UNDER_TRANSFER:
    "El tiquete está en un trasbordo activo. Primero hay que resolver el " +
    "trasbordo y luego reintentar.",
  VEHICLE_CONFLICT:
    "El vehículo ya está asignado a otra operación en ese horario.",
  SESSION_REPLACED:
    "Tu sesión se cerró porque entraron con tu mismo usuario en otro equipo.",
  // Operaciones: el backend los manda bajo detail.type (no detail.code).
  VEHICLE_INACTIVE:
    "El vehículo no está activo (desvinculado o suspendido), por eso no se puede asignar.",
  VEHICLE_DOCS_EXPIRED:
    "El vehículo tiene documentos vencidos. Actualízalos para poder asignarlo.",
  DUPLICATE_OPERATION: "Ya existe una operación igual para esa fecha y horario.",
  OPERATION_DOCUMENTS_LOCKED:
    "La operación tiene documentos bloqueados y no se puede modificar.",
  TRANSFER_REQUIRED: "Falta registrar el trasbordo antes de continuar.",
  TRANSFER_PLANILLA_LOCKED:
    "La planilla del trasbordo está bloqueada y no se puede modificar.",
  TRANSFER_STRUCTURE_LOCKED:
    "La estructura del trasbordo está bloqueada y no se puede modificar.",
  DRIVER_REASSIGNMENT_PARTIAL:
    "El conductor quedó reasignado solo parcialmente. Revisa la operación.",
};

// Mensajes por código HTTP, cuando el backend no mandó explicación.
const POR_ESTADO = {
  400: "No se pudo completar la operación. Revisa los datos e intenta de nuevo.",
  401: "Tu sesión expiró. Vuelve a iniciar sesión.",
  403: "No tienes permiso para hacer esta acción. Pídelo a un administrador.",
  404: "No encontramos la información que buscas.",
  409: "La operación choca con otro registro que ya existe.",
  410: "Este recurso ya no está disponible.",
  422: "Hay datos incompletos o inválidos. Revisa el formulario.",
  423:
    "Está bloqueado porque el período contable ya se cerró. Contabilidad debe " +
    "reabrirlo para poder modificarlo.",
  429: "Demasiadas solicitudes seguidas. Espera un momento e intenta otra vez.",
  500: "El sistema tuvo un problema procesando la solicitud. Intenta de nuevo; si sigue, repórtalo.",
  501: "Esta función todavía no está disponible.",
  502: "El servidor no respondió bien. Intenta de nuevo en un momento.",
  503: "El servicio no está disponible en este momento. Intenta más tarde.",
  504: "El servidor tardó demasiado en responder. Intenta de nuevo.",
};

const SIN_CONEXION =
  "Sin conexión con el servidor. Revisa tu internet e intenta de nuevo.";

const DEMORA =
  "El servidor se demoró más de lo normal y no confirmó la operación. " +
  "ANTES de reintentar, verifica si el movimiento quedó registrado — " +
  "reintentar a ciegas puede duplicarlo.";

// Señales de que un texto es técnico (traceback/SQL) y NO debe verlo el usuario.
const TECNICO = /traceback|psycopg|asyncpg|sqlalchemy|violates |null value|syntax error|at 0x[0-9a-f]|<class '|NoneType|KeyError|column .* does not exist/i;

const esTecnico = (t) => typeof t === "string" && TECNICO.test(t);

/** Aplana el array de errores de validación de FastAPI/pydantic (422). */
function desdeArrayValidacion(detail) {
  return detail
    .map((e) => {
      const campo = Array.isArray(e?.loc)
        ? e.loc.filter((p) => p !== "body").join(".")
        : "";
      const msg = e?.msg || "dato inválido";
      return campo ? `${campo}: ${msg}` : msg;
    })
    .join(" · ");
}

/**
 * Devuelve el motivo legible de un error de API.
 * @param {any} err error de axios / fetch / genérico
 * @param {string} [fallback] texto propio de la pantalla si no hay nada mejor
 */
export function apiErrorMessage(err, fallback) {
  if (!err) return fallback || POR_ESTADO[500];

  // 1) Sin respuesta del servidor: distinguir TIMEOUT de falta de internet.
  // No es lo mismo "no hay señal" que "el servidor se demoró": en el segundo caso
  // la operación pudo haberse ejecutado y reintentar a ciegas puede duplicar
  // dinero (cierres de caja, anulaciones). Por eso el aviso es distinto.
  const esTimeout =
    err.code === "ECONNABORTED" ||
    err.code === "ETIMEDOUT" ||
    (typeof err.message === "string" && /timeout/i.test(err.message));
  if (esTimeout) return DEMORA;
  if (err.isNetworkError || (!err.response && err.request)) return SIN_CONEXION;
  if (!err.response) {
    if (err.message === "Network Error") return SIN_CONEXION;
    // Error propio de JS (no del API): sirve tal cual si no es técnico.
    if (err.message && !esTecnico(err.message)) return err.message;
    return fallback || POR_ESTADO[500];
  }

  const status = err.response.status;
  const data = err.response.data;
  const detail = data?.detail;

  // 2) Validación 422 de pydantic: detail es un array de objetos.
  if (Array.isArray(detail) && detail.length) return desdeArrayValidacion(detail);

  // 3) detail como OBJETO { code, message } — es la forma estructurada del backend.
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
    // Varios módulos mandan el motivo bajo `error` (bloqueos de impresión de
    // planillas) en vez de `message`.
    if (typeof detail.error === "string" && detail.error && !esTecnico(detail.error))
      return detail.error;
    if (detail.code && POR_CODIGO[detail.code]) return POR_CODIGO[detail.code];
    // Operaciones manda el código bajo `type` (VEHICLE_CONFLICT, VEHICLE_INACTIVE…).
    if (detail.type && POR_CODIGO[detail.type]) return POR_CODIGO[detail.type];
    if (typeof detail.detail === "string" && detail.detail) return detail.detail;
  }

  // 4) detail como TEXTO.
  if (typeof detail === "string" && detail.trim()) {
    const txt = detail.trim();
    // 4a) "CODIGO|resto" (ej. VEHICLE_CONFLICT|...) → mensaje del código.
    const conBarra = txt.split("|")[0];
    if (POR_CODIGO[conBarra]) return POR_CODIGO[conBarra];
    // 4b) repr de Python: "423: {'code': 'PERIOD_CLOSED', 'message': '...'}"
    const m = txt.match(/'message':\s*'([^']+)'/);
    if (m) return m[1];
    const c = txt.match(/'code':\s*'([A-Z_]+)'/);
    if (c && POR_CODIGO[c[1]]) return POR_CODIGO[c[1]];
    // 4c) código suelto sin texto (ej. "PERIOD_CLOSED").
    if (POR_CODIGO[txt]) return POR_CODIGO[txt];
    // 4d) texto normal del backend — se muestra salvo que sea técnico.
    if (!esTecnico(txt)) return txt;
  }

  // 5) Otras formas que usan algunos módulos.
  if (typeof data?.message === "string" && data.message && !esTecnico(data.message))
    return data.message;
  if (typeof data?.error === "string" && data.error && !esTecnico(data.error))
    return data.error;

  // 6) Nada legible: mensaje por código HTTP (nunca "status code NNN").
  return POR_ESTADO[status] || fallback || `No se pudo completar la operación (error ${status}).`;
}

/** Título e ícono para el modal, según el tipo de error. */
export function apiErrorTitle(err) {
  const status = err?.response?.status ?? err?.status ?? 0;
  const detail = err?.response?.data?.detail;
  const code =
    (detail && typeof detail === "object" && detail.code) ||
    (typeof detail === "string" && detail.match(/'code':\s*'([A-Z_]+)'/)?.[1]) ||
    null;

  if (code === "EXTRACTO_CLOSED") return { icon: "warning", title: "Extracto cerrado" };
  if (status === 423 || code === "PERIOD_CLOSED")
    return { icon: "warning", title: "Período cerrado" };
  if (status === 403) return { icon: "warning", title: "Sin permiso" };
  if (status === 401) return { icon: "info", title: "Sesión expirada" };
  if (status === 404) return { icon: "info", title: "No encontrado" };
  if ([400, 409, 422].includes(status)) return { icon: "warning", title: "Aviso" };
  if (err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT")
    return { icon: "warning", title: "El servidor se demoró" };
  if (err?.isNetworkError || status === 0) return { icon: "warning", title: "Sin conexión" };
  if (status >= 500) return { icon: "error", title: "Error del sistema" };
  return { icon: "error", title: "Error" };
}

export default apiErrorMessage;
